import React, { useMemo } from 'react';
import CodeSelector from './CodeSelector';
import { withToastManager } from 'ui/toast';
import truncate from '../../../utils/truncate';
import { useRecursiveColorChange } from 'hooks/codes/mutations/useUpdateCode';
import useCodesSidebar from 'hooks/codes/useCodesSidebar';
import useNestCode from 'hooks/codes/mutations/useNestCode';
import { canDropOnParent } from 'utils/nestingHelpers';
import useTrackingPage from 'hooks/useTrackingPage';

const CodeNester = (props) => {
    const { projectId, codeId, codeName, open, setOpen, toastManager, maxDepth } = props;
    const truncatedCodeName = truncate(codeName, 30);

    // Get codes using the React Query hook
    const { codes: allCodes, isLoading } = useCodesSidebar(open ? projectId : null);
    
    const options = useMemo(() => {
        if (!open || !allCodes || allCodes.length === 0) return [];
        
        // Filter out the current code and any codes that would create invalid nesting
        return allCodes.filter(code => {
            // Don't allow nesting under itself
            if (code.id === codeId) return false;
            
            // Additional filtering logic if needed
            return true;
        }).map((code) => ({
            ...code,
            canDropOnParent: canDropOnParent(maxDepth, code.level)
        }));
    }, [open, allCodes, codeId, maxDepth]);

    const { propagateColorToChildren } = useRecursiveColorChange(projectId, codeId);
    
    // Get the mutation function for updating code parent
    const updateCodeParentMutation = useNestCode();
    const page = useTrackingPage();

    // Handle selection change
    const handleSelectionChange = (selectedCode) => {
        // Show toast if selected code is the same as the one being nested
        if (selectedCode.id === codeId) {
            toastManager.add("Cannot nest code under itself", { appearance: 'warning', autoDismiss: true });
            return;
        }
        // Show toast if nesting under selected code would cause more than three layers of nesting
        const isInvalidNesting = selectedCode?.canDropOnParent === false;
        if (isInvalidNesting) {
            toastManager.add("Codes cannot be nested more than three layers", { appearance: 'warning', autoDismiss: true });
            return;
        }
        // Close the selector if selection is valid
        setOpen(false);
        
        // Use the mutation to update code parent
        const parentId = selectedCode?.id;
        const position = 0;

        // truncate code names for toast
        const truncatedSelectedCode = truncate(selectedCode?.name, 30);
        // show success message optimistically to orient user in case of long code list
        toastManager.add(`"${truncatedCodeName}" moved under "${truncatedSelectedCode}"`, 
            { appearance: 'success', autoDismiss: true }
        );
        
        updateCodeParentMutation.mutate(
            {
                codeId,
                parent_id: parentId,
                position,
                trackingContext: { page, surface: 'dropdown' },
            },
            {
                onError: (error) => {
                    toastManager.add(`Failed to move code: ${error.message || 'Unknown error'}`, 
                        { appearance: 'error', autoDismiss: true }
                    );
                }
            }
        );

        propagateColorToChildren(parentId);
    }

    return (
        <CodeSelector 
            {...props}
            options={options}
            handleSelectionChange={handleSelectionChange}
            title={`Move "${truncatedCodeName}" Under`}
            shouldShowDisabled={true}
            maxListHeight={'50vh'}
            placement='left'
            isLoading={isLoading && open}
        />
    );
}

export default withToastManager(CodeNester);